import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Frequencia } from '../../core/api/frequencia';
import { FrequenciaService } from '../../core/api/frequencia.service';
import { PessoaService } from '../../core/api/pessoa.service';
import { TurmaUsuarioService } from '../../core/api/turma-usuario.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  NbButtonModule,
  NbCardModule,
  NbCheckboxModule,
  NbDialogService,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { AlunoFrequencia } from '../../core/api/aluno-frequencia';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { PessoaTelefoneEditDialogComponent } from './pessoa-telefone-edit-dialog.component';

import { mensagemDeErro } from '../../utils/mensagem-erro';
@Component({
  selector: 'app-frequencia',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NbCardModule,
    NbButtonModule,
    NbInputModule,
    NbCheckboxModule,
    NbSpinnerModule,
  ],
  templateUrl: './frequencia.component.html',
  styleUrls: ['./frequencia.component.scss'],
})
export class FrequenciaComponent implements OnInit, OnDestroy {
  @ViewChild('fotoInput') fotoInput?: ElementRef<HTMLInputElement>;
  @ViewChild('videoPreview') videoPreview?: ElementRef<HTMLVideoElement>;

  frequencia: Frequencia | null = null;
  turmaId!: string;
  /** yyyy-MM-dd */
  data = this.hojeISO();

  carregando = false;
  persistindo = false;
  private persistenciasPendentes = 0;
  errosCarregar: string | null = null;

  /**
   * A chamada só pode ser editada por quem é catequista **desta** turma — ou seja, por quem tem
   * vínculo `TurmaUsuario` com ela (`GET /turmas-usuario/minhas`). Sem vínculo a tela fica em
   * modo leitura. Começa `false` e só abre depois da verificação (falha ⇒ continua bloqueado).
   */
  podeEditar = false;
  verificandoVinculo = false;

  /** pessoaId cuja foto está sendo enviada */
  uploadFotoParaPessoaId: string | null = null;
  private alunoFotoAlvo: AlunoFrequencia | null = null;

  /** UI da câmera (getUserMedia) */
  cameraAberta = false;
  alunoCameraAlvo: AlunoFrequencia | null = null;
  cameraInicializando = false;
  private streamMidia: MediaStream | null = null;

  /** Popup ver foto (GET /pessoas/{pessoaId}/foto + fotoDocumentoId na query) */
  fotoPopupAberta = false;
  fotoPopupUrl: string | null = null;
  fotoPopupTitulo = '';
  fotoPopupDocumentoId: string | null = null;

  constructor(
    private frequenciaService: FrequenciaService,
    private pessoaService: PessoaService,
    private turmaUsuarioService: TurmaUsuarioService,
    private dialogService: NbDialogService,
    private route: ActivatedRoute,
    private router: Router,
    private toastr: NbToastrService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnDestroy(): void {
    this.encerrarStreamMidia();
  }

  ngOnInit(): void {
    this.turmaId = this.route.snapshot.paramMap.get('turmaId') ?? '';
    // `?data=yyyy-MM-dd` permite abrir direto num encontro (ex.: clique na coluna da matriz).
    const dataUrl = this.route.snapshot.queryParamMap.get('data');
    if (dataUrl && /^\d{4}-\d{2}-\d{2}$/.test(dataUrl)) {
      this.data = dataUrl;
    }
    this.verificarVinculoComTurma();
    this.carregarFrequencia();
  }

  /** Confere se a turma da URL está entre as turmas do usuário logado (vínculo TurmaUsuario). */
  private verificarVinculoComTurma(): void {
    const turmaId = this.turmaId?.trim();
    if (!turmaId) {
      this.podeEditar = false;
      return;
    }
    this.verificandoVinculo = true;
    this.turmaUsuarioService
      .minhasTurmas()
      .pipe(finalize(() => (this.verificandoVinculo = false)))
      .subscribe({
        next: (turmas) => {
          this.podeEditar = (turmas ?? []).some(
            (t) => String(t?.id ?? '').trim() === turmaId,
          );
          this.cdr.detectChanges();
        },
        error: () => {
          // Falhou a verificação: mantém bloqueado em vez de liberar por engano.
          this.podeEditar = false;
          this.toastr.warning(
            'Não foi possível confirmar seu vínculo com a turma; a chamada ficou em modo leitura.',
            'Frequência',
          );
          this.cdr.detectChanges();
        },
      });
  }

  private hojeISO(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  carregarFrequencia(): void {
    if (!this.turmaId) {
      this.errosCarregar = 'Turma não informada na URL.';
      return;
    }
    this.errosCarregar = null;
    this.carregando = true;
    this.frequenciaService.carregarFrequencia(this.turmaId, this.data).subscribe({
      next: (f) => {
        f.alunos = this.ordenarAlfabeticamente(f.alunos ?? []);
        f.turmaId = f.turmaId ?? this.turmaId;
        f.data = f.data ?? this.data;
        this.frequencia = f;
        this.carregando = false;
      },
      error: (err) => {
        this.carregando = false;
        this.frequencia = null;
        const msg =
          mensagemDeErro(err, 'Não foi possível carregar a frequência.');
        this.errosCarregar = msg;
        this.toastr.danger(msg, 'Erro');
      },
    });
  }

  private ordenarAlfabeticamente(alunos: AlunoFrequencia[]): AlunoFrequencia[] {
    return [...alunos].sort((a, b) => {
      const nomeA = a.pessoaNome ?? '';
      const nomeB = b.pessoaNome ?? '';
      return nomeA.localeCompare(nomeB, 'pt-BR', { sensitivity: 'base' });
    });
  }

  /** Persiste só a linha alterada; não recarrega a lista. */
  private persistir(linha: AlunoFrequencia): Observable<void> {
    if (!this.frequencia) return of(undefined);
    const ctx = {
      turmaId: this.frequencia.turmaId ?? this.turmaId,
      data: this.frequencia.data ?? this.data,
      turmaNome: this.frequencia.turmaNome,
    };
    this.persistenciasPendentes++;
    this.persistindo = true;
    return this.frequenciaService.salvarLinha(linha, ctx).pipe(
      finalize(() => {
        this.persistenciasPendentes--;
        this.persistindo = this.persistenciasPendentes > 0;
      }),
      catchError((err) => {
        const msg =
          mensagemDeErro(err, 'Não foi possível salvar a frequência.');
        this.toastr.danger(msg, 'Erro ao salvar');
        return throwError(() => err);
      }),
    );
  }

  onPresenteChange(aluno: AlunoFrequencia, novo: boolean): void {
    if (!this.garantirPermissaoEdicao()) {
      // Desfaz o toggle otimista do checkbox.
      this.cdr.detectChanges();
      return;
    }
    const anterior = aluno.presente === true;
    aluno.presente = novo;
    this.persistir(aluno).subscribe({
      error: () => {
        aluno.presente = anterior;
      },
    });
  }

  onPresenteMissaChange(aluno: AlunoFrequencia, novo: boolean): void {
    if (!this.garantirPermissaoEdicao()) {
      this.cdr.detectChanges();
      return;
    }
    const anterior = aluno.presenteMissa === true;
    aluno.presenteMissa = novo;
    this.persistir(aluno).subscribe({
      error: () => {
        aluno.presenteMissa = anterior;
      },
    });
  }

  /** Rede de segurança: os checkboxes já ficam desabilitados sem vínculo. */
  private garantirPermissaoEdicao(): boolean {
    if (this.podeEditar) return true;
    this.toastr.warning(
      'Somente os catequistas desta turma podem registrar a frequência.',
      'Sem permissão',
    );
    return false;
  }

  temFoto(aluno: AlunoFrequencia): boolean {
    const id = aluno.fotoDocumentoId;
    return !!id && String(id).trim() !== '';
  }

  temTelefonePessoa(aluno: AlunoFrequencia): boolean {
    const t = aluno.pessoaTelefone;
    return t != null && String(t).trim() !== '';
  }

  textoBotaoTelefone(aluno: AlunoFrequencia): string {
    return this.temTelefonePessoa(aluno) ? String(aluno.pessoaTelefone).trim() : 'Telefone';
  }

  tituloBotaoTelefone(aluno: AlunoFrequencia): string {
    return this.temTelefonePessoa(aluno)
      ? 'Editar telefone: ' + String(aluno.pessoaTelefone).trim()
      : 'Informar telefone';
  }

  abrirEditarTelefone(aluno: AlunoFrequencia): void {
    if (!aluno.pessoaId?.trim()) {
      this.toastr.warning('Pessoa sem identificador.', 'Telefone');
      return;
    }
    this.dialogService
      .open(PessoaTelefoneEditDialogComponent, {
        context: {
          pessoaId: aluno.pessoaId,
          pessoaNome: aluno.pessoaNome ?? '',
          telefoneInicial: aluno.pessoaTelefone ?? '',
        },
      })
      .onClose.subscribe((res: string | false | undefined) => {
        if (res === false || res === undefined) return;
        aluno.pessoaTelefone = String(res).trim() !== '' ? String(res).trim() : null;
        this.cdr.detectChanges();
      });
  }

  verFoto(aluno: AlunoFrequencia): void {
    if (!aluno.pessoaId) {
      this.toastr.warning('Pessoa sem identificador.', 'Foto');
      return;
    }
    if (!this.temFoto(aluno)) return;
    const docId = String(aluno.fotoDocumentoId).trim();
    this.fotoPopupTitulo = aluno.pessoaNome ?? 'Foto';
    this.fotoPopupDocumentoId = docId;
    const base = this.pessoaService.urlFotoPessoa(aluno.pessoaId);
    this.fotoPopupUrl = `${base}?t=${Date.now()}&fotoDocumentoId=${encodeURIComponent(docId)}`;
    this.fotoPopupAberta = true;
    this.cdr.detectChanges();
  }

  fecharFotoPopup(): void {
    this.fotoPopupAberta = false;
    this.fotoPopupUrl = null;
    this.fotoPopupTitulo = '';
    this.fotoPopupDocumentoId = null;
    this.cdr.detectChanges();
  }

  onFotoPopupErro(): void {
    this.toastr.danger('Não foi possível carregar a foto.', 'Erro');
    this.fecharFotoPopup();
  }

  async abrirCamera(aluno: AlunoFrequencia): Promise<void> {
    if (!aluno.pessoaId) {
      this.toastr.warning('Pessoa sem identificador.', 'Foto');
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      this.toastr.warning('Câmera não disponível neste navegador. Escolha um arquivo.', 'Foto');
      this.abrirSeletorArquivo(aluno);
      return;
    }

    this.encerrarStreamMidia();
    this.alunoCameraAlvo = aluno;
    this.cameraAberta = true;
    this.cameraInicializando = true;
    this.cdr.detectChanges();

    try {
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 } },
          audio: false,
        });
      }
      this.streamMidia = stream;
      this.cdr.detectChanges();
      setTimeout(() => this.vincularVideoAoStream(0), 0);
    } catch {
      this.cameraAberta = false;
      this.alunoCameraAlvo = null;
      this.cameraInicializando = false;
      this.cdr.detectChanges();
      this.toastr.warning(
        'Não foi possível acessar a câmera. Verifique permissões ou escolha um arquivo.',
        'Foto',
      );
      this.abrirSeletorArquivo(aluno);
    }
  }

  private vincularVideoAoStream(tentativa: number): void {
    const video = this.videoPreview?.nativeElement;
    if (video && this.streamMidia) {
      video.srcObject = this.streamMidia;
      video.muted = true;
      video.setAttribute('playsinline', 'true');
      video.play().catch(() => {});
      this.cameraInicializando = false;
      this.cdr.detectChanges();
      return;
    }
    if (tentativa < 8) {
      setTimeout(() => this.vincularVideoAoStream(tentativa + 1), 50);
      return;
    }
    this.cameraInicializando = false;
    this.cdr.detectChanges();
  }

  private encerrarStreamMidia(): void {
    this.streamMidia?.getTracks().forEach((t) => t.stop());
    this.streamMidia = null;
    const v = this.videoPreview?.nativeElement;
    if (v) {
      v.srcObject = null;
    }
  }

  cancelarCamera(): void {
    this.encerrarStreamMidia();
    this.cameraAberta = false;
    this.alunoCameraAlvo = null;
    this.cameraInicializando = false;
    this.cdr.detectChanges();
  }

  capturarDaCamera(): void {
    const aluno = this.alunoCameraAlvo;
    const video = this.videoPreview?.nativeElement;
    if (!aluno?.pessoaId || !video) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (!w || !h) {
      this.toastr.warning('Aguarde a câmera iniciar e tente de novo.', 'Foto');
      return;
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.toastr.danger('Não foi possível capturar a imagem.', 'Erro');
      return;
    }
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.88);
    const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1]! : dataUrl;
    this.cancelarCamera();
    this.enviarFotoBase64(aluno, base64);
  }

  /** Sem câmera ao vivo: galeria ou arquivo (também funciona no desktop). */
  escolherArquivoEmVezDaCamera(): void {
    const aluno = this.alunoCameraAlvo;
    this.cancelarCamera();
    if (aluno) this.abrirSeletorArquivo(aluno);
  }

  private abrirSeletorArquivo(aluno: AlunoFrequencia): void {
    this.alunoFotoAlvo = aluno;
    this.fotoInput?.nativeElement.click();
  }

  onFotoSelecionada(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const aluno = this.alunoFotoAlvo;
    this.alunoFotoAlvo = null;
    input.value = '';

    if (!file || !aluno?.pessoaId) return;

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1]! : dataUrl;
      this.enviarFotoBase64(aluno, base64);
    };
    reader.onerror = () => {
      this.toastr.danger('Não foi possível ler a imagem.', 'Erro');
    };
    reader.readAsDataURL(file);
  }

  private enviarFotoBase64(aluno: AlunoFrequencia, base64: string): void {
    this.uploadFotoParaPessoaId = aluno.pessoaId!;
    this.pessoaService
      .uploadFoto(aluno.pessoaId!, { imagemBase64: base64 })
      .pipe(finalize(() => (this.uploadFotoParaPessoaId = null)))
      .subscribe({
        next: (res) => {
          if (res?.fotoDocumentoId) {
            aluno.fotoDocumentoId = String(res.fotoDocumentoId);
          }
          this.toastr.success('Foto enviada com sucesso.');
        },
        error: (err) => {
          const msg =
            mensagemDeErro(err, 'Não foi possível enviar a foto.');
          this.toastr.danger(msg, 'Erro');
        },
      });
  }

  onDataChange(): void {
    // Mantém a URL em sincronia para o endereço continuar compartilhável/recarregável.
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { data: this.data },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
    this.carregarFrequencia();
  }

  get dataFormatada(): string {
    const partes = this.data.split('-');
    if (partes.length !== 3) return this.data;
    return `${partes[2]}/${partes[1]}/${partes[0]}`;
  }

  get totalFaltas(): number {
    return this.frequencia?.alunos?.filter((a) => a.presente !== true).length ?? 0;
  }

  get totalPresentes(): number {
    return this.frequencia?.alunos?.filter((a) => a.presente === true).length ?? 0;
  }

  get totalPresentesMissa(): number {
    return this.frequencia?.alunos?.filter((a) => a.presenteMissa === true).length ?? 0;
  }

  /** Itinerário da turma exige registro em missa (campo repetido por aluno na API). */
  get frequenciaMissaAtiva(): boolean {
    return this.frequencia?.alunos?.some((a) => a.frequenciaMissa === true) ?? false;
  }
}
